import { LOCALES } from 'config/constants/locales';
import { fetchContentfulEntries, fetchContentfulEntry } from 'connectors/contentful';
import { HOME_HANDLE } from 'constants/contentful';
import { IProductPages, IProductPagesFields } from 'global';
import HomeHead from 'modules/landingpage/home/home-head';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { NEXT_LOCALES } from 'utils/i18n/next-locales';
import { getLocaleFromNextLocale, REGIONS } from 'utils/market';
import Landingpage from '../../modules/landingpage/home';
import withFallback from '../../utils/with-fallback';

const Home: NextPage<{
  contentfulData: IProductPages;
}> = ({ contentfulData }) => {
  if (!contentfulData?.fields) {
    console.error('Home: contentfulData is undefined');
    return null;
  }

  return (
    <>
      <HomeHead contentfulData={contentfulData} />
      <Landingpage />
    </>
  );
};

export const getStaticProps: GetStaticProps = async ({ params, locale: nextLocale }) => {
  const locale = getLocaleFromNextLocale(params?.region, nextLocale);
  const contentfulHandle = HOME_HANDLE;
  const startTime = Date.now();

  console.log(
    `[Performance] Starting getStaticProps for handle: ${contentfulHandle}, locale: ${locale}`
  );
  console.time(`getStaticProps-${contentfulHandle}-${locale}`);

  try {
    console.log(`[Performance] Starting Contentful fetch for handle: ${contentfulHandle}`);
    console.time(`contentful-fetch-${contentfulHandle}-${locale}`);

    // `/` is fixed: `home` identifies the entry, not a localized URL slug.
    const { items } = await fetchContentfulEntries<IProductPagesFields>({
      content_type: 'productPages',
      'fields.handle': contentfulHandle,
      locale: LOCALES.DE,
      select: 'sys.id',
      include: 0,
      limit: 1,
    });
    const contentfulData = items[0]
      ? await fetchContentfulEntry<IProductPagesFields>(items[0].sys.id, locale)
      : null;

    console.timeEnd(`contentful-fetch-${contentfulHandle}-${locale}`);
    const contentfulFetchTime = Date.now() - startTime;
    console.log(`[Performance] Contentful fetch completed in ${contentfulFetchTime}ms`);

    if (contentfulData === null) {
      console.log(`[Performance] No data found for handle: ${contentfulHandle}`);
      console.timeEnd(`getStaticProps-${contentfulHandle}-${locale}`);
      return { notFound: true, revalidate: true };
    }

    const totalTime = Date.now() - startTime;
    console.log(`[Performance] getStaticProps completed successfully in ${totalTime}ms`);
    console.timeEnd(`getStaticProps-${contentfulHandle}-${locale}`);

    return {
      props: {
        contentfulData,
      },
      revalidate: 600, // 10 minutes
    };
  } catch (error) {
    const errorTime = Date.now() - startTime;
    console.error(
      `[Performance] Error in getStaticProps after ${errorTime}ms for page: ${contentfulHandle}`,
      error
    );
    console.timeEnd(`getStaticProps-${contentfulHandle}-${locale}`);
    return { notFound: true, revalidate: true };
  }
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: REGIONS.flatMap((region) => [
    { params: { region } },
    ...NEXT_LOCALES.map((locale) => ({ params: { region }, locale })),
  ]),
  fallback: 'blocking',
});

export default withFallback(Home);
